import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Save, 
  Percent,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react'
import { api } from '../services/api'
import ConfirmationDialog from '../components/ConfirmationDialog'

interface CommissionSettingsData {
  commissionPercentage: number
  isActive: boolean
}

const CommissionSettings = () => {
  const [settings, setSettings] = useState<CommissionSettingsData>({
    commissionPercentage: 10,
    isActive: true,
  })
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(true)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setFetching(true)
      const response = await api.get('/commission')
      if (response.data.success) {
        setSettings({
          commissionPercentage: response.data.data.commissionPercentage || 10,
          isActive: response.data.data.isActive !== false,
        })
      }
    } catch (err: any) {
      console.error('Error fetching commission settings:', err)
      setError(err.response?.data?.message || 'Failed to fetch commission settings')
    } finally {
      setFetching(false)
    }
  }

  const handleSaveClick = () => {
    if (settings.commissionPercentage < 0 || settings.commissionPercentage > 100) {
      setError('Commission percentage must be between 0 and 100')
      return
    }
    setShowConfirmDialog(true)
  }

  const handleConfirmSave = async () => {
    setShowConfirmDialog(false)
    setLoading(true)
    setError(null)
    try {
      const response = await api.put('/commission', {
        commissionPercentage: settings.commissionPercentage,
        isActive: settings.isActive,
      })
      if (response.data.success) {
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      }
    } catch (err: any) {
      console.error('Error saving commission settings:', err)
      setError(err.response?.data?.message || 'Failed to save commission settings')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof CommissionSettingsData, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }))
    setError(null)
  }

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Commission Settings</h1>
          <p className="text-gray-600">Configure platform commission percentage for course bookings</p>
        </div>
        <button
          onClick={handleSaveClick}
          disabled={loading}
          className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-amber-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg flex items-center space-x-2"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="w-4 h-4" />
          )}
          <span>{loading ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      {saved && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3"
        >
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800 font-medium">Commission settings saved successfully!</span>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3"
        >
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800 font-medium">{error}</span>
        </motion.div>
      )}

      {/* Commission Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
      >
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Percent className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Platform Commission</h3>
          </div>
          <p className="text-gray-600">Set the percentage commission the platform takes from each booking</p>
        </div>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Commission Percentage (%)
            </label>
            <div className="relative">
              <input
                type="number"
                value={settings.commissionPercentage}
                onChange={(e) => handleInputChange('commissionPercentage', parseFloat(e.target.value) || 0)}
                min="0"
                max="100"
                step="0.1"
                className="w-full px-4 py-3 pl-10 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all duration-200 bg-gray-50"
                placeholder="10"
              />
              <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Enter a value between 0 and 100. For example, 10 means 10% commission.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isActive"
              checked={settings.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            <label htmlFor="isActive" className="text-sm font-semibold text-gray-700">
              Commission is Active
            </label>
          </div>
        </div>
      </motion.div>

      {/* Information Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-blue-50 border border-blue-200 rounded-2xl p-6"
      >
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Info className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-blue-900 mb-2">How Commission Works</h4>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>When a student books, they pay the booking amount (set by the school) online through the platform.</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  Commission is calculated as a percentage of the <strong>total course fee</strong>, not the booking
                  amount alone, and is deducted from the online payment.
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>The school receives the booking amount minus the commission (10% of course fee).</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  Example: course fee ₹50,000; commission 10% = ₹5,000; booking amount ₹15,000 (more than 10% of course
                  fee). Student pays ₹15,000 online; platform keeps ₹5,000; school receives ₹10,000; student pays ₹35,000
                  on arrival directly to the school.
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  Booking amount must be above 10% of the course fee (up to the full fee) so the online payment can cover
                  the platform commission.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Example Calculation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
      >
        <div className="mb-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Example Calculation</h3>
          </div>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Course Price (Single Room):</span>
            <span className="font-semibold text-gray-900">₹50,000</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-700">Platform Commission ({settings.commissionPercentage}%):</span>
            <span className="font-semibold text-amber-600">₹{(50000 * settings.commissionPercentage / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
          </div>
          <div className="border-t border-gray-300 pt-3 flex justify-between items-center">
            <span className="text-gray-700 font-semibold">Amount to School:</span>
            <span className="font-bold text-green-600">₹{(50000 * (1 - settings.commissionPercentage / 100)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-300">
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> If the school sets initial booking amount as 30% (₹15,000), 
              the user pays ₹15,000 to the platform. Platform takes ₹{((15000 * settings.commissionPercentage / 100)).toLocaleString('en-IN', { maximumFractionDigits: 2 })} 
              {' '}and transfers ₹{((15000 * (1 - settings.commissionPercentage / 100))).toLocaleString('en-IN', { maximumFractionDigits: 2 })} to the school. 
              The remaining ₹35,000 is paid directly to the school by the user.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmSave}
        title="Update Commission Settings"
        message={`Are you sure you want to update the commission percentage to ${settings.commissionPercentage}%?`}
        details={`This will affect all future bookings.\n\nThe change will take effect immediately.\n\nExample: For a ₹50,000 course, the platform will take ₹${(50000 * settings.commissionPercentage / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })} as commission.`}
        confirmText="Yes, Update"
        cancelText="Cancel"
        type="warning"
        isLoading={loading}
        icon={<Percent className="w-6 h-6" />}
      />
    </div>
  )
}

export default CommissionSettings

